import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Search, Download, Heart } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useFavorites } from "@/lib/favorites";
import SearchDialog from "@/components/SearchDialog";
import { BrandMark } from "@/components/common";

export const NAV = [
  { to: "/programacao", label: "Programação" },
  { to: "/mapa", label: "Mapa" },
  { to: "/organizacoes", label: "Organizações" },
  { to: "/semana", label: "Semana da Saúde Mental", short: "A Semana" },
  { to: "/saude-mental", label: "Saúde Mental para Todos", short: "Saúde Mental" },
  { to: "/noticias", label: "Notícias" },
  { to: "/galeria", label: "Galeria" },
  { to: "/informacoes", label: "Informações" },
];

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3 min-h-[44px] min-w-0" data-testid="header-logo" aria-label="Feira de Saúde e Bem-Estar — página inicial">
      <BrandMark className="h-10 w-9 shrink-0" />
      <span className="leading-none">
        <span className="block font-display text-[0.9rem] sm:text-[1rem] leading-tight sm:whitespace-nowrap uppercase text-[var(--ink)]"><span className="font-light">Feira de </span><span className="font-extrabold">Saúde e Bem-Estar</span></span>
        <span className="block eyebrow text-[0.58rem] sm:text-[0.62rem] text-muted-foreground mt-1 hidden sm:block sm:whitespace-nowrap">Semana da Saúde Mental · 2026</span>
      </span>
    </Link>
  );
}

function useInstall() {
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  return evt ? async () => { evt.prompt(); await evt.userChoice; setEvt(null); } : null;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const install = useInstall();
  const { ids } = useFavorites();
  const linkCls = ({ isActive }) => `px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "underline decoration-[3px] decoration-[var(--sun)] underline-offset-8 font-bold" : "hover:underline underline-offset-8"}`;

  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="container-x flex items-center justify-between gap-4 h-16 lg:h-20">
        <Logo />
        <nav className="hidden xl:flex items-center gap-1" aria-label="Navegação principal">
          {NAV.slice(0, 6).map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.to.slice(1)}`}>{n.short || n.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-1 shrink-0">
          {install && (
            <button onClick={install} className="hidden sm:inline-flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-medium hover:bg-black/5" data-testid="pwa-install-button">
              <Download className="h-4 w-4" aria-hidden /> Instalar app
            </button>
          )}
          <button onClick={() => setSearch(true)} className="h-11 w-11 grid place-items-center rounded-full hover:bg-black/5" aria-label="Pesquisar" data-testid="header-search-button"><Search className="h-5 w-5" /></button>
          <Link to="/a-minha-feira" className="relative h-11 w-11 grid place-items-center rounded-full hover:bg-black/5" aria-label={`A minha Feira, ${ids.length} atividades`} data-testid="header-favorites-link">
            <Heart className="h-5 w-5" />
            {ids.length > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--clay)] text-white text-[10px] font-bold grid place-items-center">{ids.length}</span>}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="h-11 w-11 grid place-items-center rounded-full hover:bg-black/5" aria-label="Abrir menu" data-testid="header-menu-button"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-[88vw] sm:max-w-md overflow-y-auto">
              <SheetTitle className="eyebrow text-muted-foreground">Menu</SheetTitle>
              <nav className="mt-6 flex flex-col" aria-label="Menu completo">
                {[{ to: "/", label: "Início" }, ...NAV, { to: "/a-minha-feira", label: "A minha Feira" }, { to: "/agora", label: "Agora na Feira" }, { to: "/memoria", label: "Memória da Feira", dev: true }].map((n) => (
                  <NavLink key={n.to} to={n.to} end onClick={() => setOpen(false)} data-testid={`menu-link-${n.to.slice(1) || "inicio"}`}
                    className={({ isActive }) => `font-display text-2xl font-bold py-3 border-b border-black/5 flex items-center gap-3 flex-wrap ${isActive ? "text-[var(--leaf)]" : ""}`}>{n.label}{n.dev && <span className="font-sans text-[11px] font-semibold rounded-full border border-dashed border-amber-700/60 bg-amber-50 px-2 py-0.5 text-amber-900" data-testid="menu-memoria-dev">Em desenvolvimento</span>}</NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <div className="h-1.5 brand-stripe" aria-hidden />
      <SearchDialog open={search} onOpenChange={setSearch} />
    </header>
  );
}
